/**
 * "Add to Home Screen" install prompt orchestration.
 *
 * The browser fires `beforeinstallprompt` only when Chrome's install
 * criteria are met (manifest valid, icons present, served over HTTPS,
 * not already installed). We capture the event, suppress the auto-banner,
 * and surface our own UI so the user can install at a moment of their
 * choosing.
 *
 * On Android Chrome / desktop Chrome / Edge: this works fully.
 * On iOS Safari: `beforeinstallprompt` does not fire — users must use
 * Share → "Add to Home Screen". We detect that and could show a hint
 * (deferred to a future polish pass).
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

type Listener = (state: { available: boolean; installed: boolean }) => void;

class InstallPromptManager {
  private deferred: BeforeInstallPromptEvent | null = null;
  private installed = false;
  private listeners = new Set<Listener>();

  init(): void {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferred = e as BeforeInstallPromptEvent;
      this.emit();
    });

    window.addEventListener('appinstalled', () => {
      this.deferred = null;
      this.installed = true;
      this.emit();
    });

    // If already running in standalone mode (already installed), reflect that.
    if (window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as any).standalone === true) {
      this.installed = true;
    }
  }

  isAvailable(): boolean { return this.deferred !== null && !this.installed; }
  isInstalled(): boolean { return this.installed; }

  async trigger(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!this.deferred) return 'unavailable';
    const evt = this.deferred;
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      this.deferred = null;
      this.emit();
      return choice.outcome;
    } catch (err) {
      console.warn('[install] prompt failed', err);
      this.deferred = null;
      this.emit();
      return 'dismissed';
    }
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn({ available: this.isAvailable(), installed: this.installed });
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    const state = { available: this.isAvailable(), installed: this.installed };
    for (const l of this.listeners) l(state);
  }
}

export const installPrompt = new InstallPromptManager();
